import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  buildRecordedFile,
  isMediaRecordingSupported,
  requestRecordingStream,
  resolvePreferredRecordingMimeType,
  type MediaRecordingKind,
} from '../utils/media-recording'

export type MediaRecorderStatus =
  | 'idle'
  | 'requesting-permission'
  | 'recording'
  | 'paused'
  | 'processing'
  | 'error'

export interface MediaRecorderAudioLevels {
  left: number
  right: number
  master: number
}

const EMPTY_AUDIO_LEVELS: MediaRecorderAudioLevels = {
  left: 0,
  right: 0,
  master: 0,
}

function toMeterLevel(data: Uint8Array): number {
  let sumSquares = 0
  for (const sample of data) {
    const normalized = (sample - 128) / 128
    sumSquares += normalized * normalized
  }

  const rms = Math.sqrt(sumSquares / data.length)
  const gatedLevel = Math.max(0, rms - 0.018)
  return Math.min(1, Math.log10(1 + gatedLevel * 24) / Math.log10(25))
}

export function useMediaRecorder(kind: MediaRecordingKind) {
  const [status, setStatus] = useState<MediaRecorderStatus>('idle')
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [audioLevels, setAudioLevels] = useState<MediaRecorderAudioLevels>(EMPTY_AUDIO_LEVELS)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const startedAtRef = useRef<number>(0)
  const elapsedBeforePauseRef = useRef(0)
  const timerRef = useRef<number | null>(null)
  const finalizingActionRef = useRef<'save' | 'cancel'>('save')
  const pendingStopRef = useRef<{
    resolve: (file: File) => void
    reject: (error: Error) => void
  } | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const analyserRefs = useRef<[AnalyserNode | null, AnalyserNode | null]>([null, null])
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null)
  const splitterNodeRef = useRef<ChannelSplitterNode | null>(null)
  const audioLevelDataRefs = useRef<[Uint8Array | null, Uint8Array | null]>([null, null])
  const audioLevelFrameRef = useRef<number | null>(null)
  const smoothedAudioLevelsRef = useRef<MediaRecorderAudioLevels>(EMPTY_AUDIO_LEVELS)
  const lastAudioLevelCommitRef = useRef(0)

  const supported = useMemo(() => isMediaRecordingSupported(kind), [kind])

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const startElapsedTimer = useCallback(() => {
    clearTimer()
    timerRef.current = window.setInterval(() => {
      const segmentElapsed = startedAtRef.current > 0 ? Date.now() - startedAtRef.current : 0
      setElapsedSeconds(
        Math.max(0, Math.floor((elapsedBeforePauseRef.current + segmentElapsed) / 1000)),
      )
    }, 250)
  }, [clearTimer])

  const clearAudioMeter = useCallback(() => {
    if (audioLevelFrameRef.current !== null) {
      window.cancelAnimationFrame(audioLevelFrameRef.current)
      audioLevelFrameRef.current = null
    }

    sourceNodeRef.current?.disconnect()
    sourceNodeRef.current = null
    splitterNodeRef.current?.disconnect()
    splitterNodeRef.current = null
    for (const analyser of analyserRefs.current) {
      analyser?.disconnect()
    }
    analyserRefs.current = [null, null]
    audioLevelDataRefs.current = [null, null]
    void audioContextRef.current?.close()
    audioContextRef.current = null
    smoothedAudioLevelsRef.current = EMPTY_AUDIO_LEVELS
    lastAudioLevelCommitRef.current = 0
    setAudioLevels(EMPTY_AUDIO_LEVELS)
  }, [])

  const startAudioMeter = useCallback((stream: MediaStream) => {
    if (kind !== 'audio') {
      return
    }

    clearAudioMeter()

    const AudioContextCtor = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!AudioContextCtor) {
      return
    }

    const audioContext = new AudioContextCtor()
    const source = audioContext.createMediaStreamSource(stream)
    const primaryAnalyser = audioContext.createAnalyser()
    primaryAnalyser.fftSize = 256
    primaryAnalyser.smoothingTimeConstant = 0.82

    const trackSettings = stream.getAudioTracks()[0]?.getSettings()
    const channelCount = Math.max(
      1,
      Math.min(2, trackSettings?.channelCount ?? source.channelCount ?? 1),
    )

    let secondaryAnalyser: AnalyserNode | null = null
    if (channelCount >= 2) {
      const splitter = audioContext.createChannelSplitter(2)
      const rightAnalyser = audioContext.createAnalyser()
      rightAnalyser.fftSize = 256
      rightAnalyser.smoothingTimeConstant = 0.82

      source.connect(splitter)
      splitter.connect(primaryAnalyser, 0)
      splitter.connect(rightAnalyser, 1)
      splitterNodeRef.current = splitter
      secondaryAnalyser = rightAnalyser
    } else {
      source.connect(primaryAnalyser)
    }

    audioContextRef.current = audioContext
    sourceNodeRef.current = source
    analyserRefs.current = [primaryAnalyser, secondaryAnalyser]
    audioLevelDataRefs.current = [
      new Uint8Array(primaryAnalyser.fftSize),
      secondaryAnalyser ? new Uint8Array(secondaryAnalyser.fftSize) : null,
    ]

    const tick = () => {
      const [leftAnalyser, rightAnalyser] = analyserRefs.current
      const [leftData, rightData] = audioLevelDataRefs.current
      if (!leftAnalyser || !leftData) {
        return
      }

      leftAnalyser.getByteTimeDomainData(leftData)
      const leftLevel = toMeterLevel(leftData)

      let rightLevel = leftLevel
      if (rightAnalyser && rightData) {
        rightAnalyser.getByteTimeDomainData(rightData)
        rightLevel = toMeterLevel(rightData)
      }

      const previous = smoothedAudioLevelsRef.current
      const nextLevels = {
        left: previous.left * 0.82 + leftLevel * 0.18,
        right: previous.right * 0.82 + rightLevel * 0.18,
        master: 0,
      }
      nextLevels.master = Math.max(nextLevels.left, nextLevels.right)
      smoothedAudioLevelsRef.current = nextLevels

      const now = window.performance.now()
      if (now - lastAudioLevelCommitRef.current >= 80) {
        lastAudioLevelCommitRef.current = now
        setAudioLevels(nextLevels)
      }

      audioLevelFrameRef.current = window.requestAnimationFrame(tick)
    }

    audioLevelFrameRef.current = window.requestAnimationFrame(tick)
  }, [clearAudioMeter, kind])

  const stopTracks = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
  }, [])

  const reset = useCallback(() => {
    clearTimer()
    clearAudioMeter()
    stopTracks()
    recorderRef.current = null
    chunksRef.current = []
    startedAtRef.current = 0
    elapsedBeforePauseRef.current = 0
    setElapsedSeconds(0)
    setStatus('idle')
    setErrorMessage(null)
    if (pendingStopRef.current) {
      pendingStopRef.current.reject(new Error('录制已取消。'))
      pendingStopRef.current = null
    }
  }, [clearAudioMeter, clearTimer, stopTracks])

  const startRecording = useCallback(async (options?: { audioDeviceId?: string | null; includeMicrophone?: boolean }) => {
    if (!supported) {
      setStatus('error')
      setErrorMessage('当前浏览器不支持录制功能。请使用最新版 Chrome 或 Edge。')
      return false
    }

    clearTimer()
    stopTracks()
    chunksRef.current = []
    setErrorMessage(null)
    setElapsedSeconds(0)
    elapsedBeforePauseRef.current = 0
    setStatus('requesting-permission')

    try {
      const stream = await requestRecordingStream(kind, options)
      streamRef.current = stream
      startAudioMeter(stream)
      const mimeType = resolvePreferredRecordingMimeType(kind)
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream)

      recorderRef.current = recorder
      finalizingActionRef.current = 'save'

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data)
        }
      }

      recorder.onerror = () => {
        clearTimer()
        stopTracks()
        setStatus('error')
        setErrorMessage('录制过程中发生错误，请重试。')
        if (pendingStopRef.current) {
          pendingStopRef.current.reject(new Error('录制过程中发生错误，请重试。'))
          pendingStopRef.current = null
        }
      }

      recorder.onstop = () => {
        const action = finalizingActionRef.current
        clearTimer()
        clearAudioMeter()
        stopTracks()
        recorderRef.current = null
        startedAtRef.current = 0
        elapsedBeforePauseRef.current = 0

        if (action === 'cancel') {
          chunksRef.current = []
          setStatus('idle')
          setElapsedSeconds(0)
          if (pendingStopRef.current) {
            pendingStopRef.current.reject(new Error('录制已取消。'))
            pendingStopRef.current = null
          }
          return
        }

        try {
          const resolvedMimeType = recorder.mimeType || mimeType || (kind === 'audio' ? 'audio/webm' : 'video/webm')
          const blob = new Blob(chunksRef.current, { type: resolvedMimeType })
          const file = buildRecordedFile({
            blob,
            kind,
            mimeType: resolvedMimeType,
          })
          chunksRef.current = []
          setStatus('idle')
          setElapsedSeconds(0)
          pendingStopRef.current?.resolve(file)
          pendingStopRef.current = null
        } catch (error) {
          const message = error instanceof Error ? error.message : '录制结果处理失败。'
          setStatus('error')
          setErrorMessage(message)
          pendingStopRef.current?.reject(new Error(message))
          pendingStopRef.current = null
        }
      }

      recorder.onpause = () => {
        setStatus('paused')
      }

      recorder.onresume = () => {
        setStatus('recording')
      }

      stream.getTracks().forEach((track) => {
        track.onended = () => {
          if (recorder.state !== 'inactive') {
            recorder.stop()
          }
        }
      })

      recorder.start(250)
      startedAtRef.current = Date.now()
      startElapsedTimer()
      setStatus('recording')
      return true
    } catch (error) {
      clearTimer()
      clearAudioMeter()
      stopTracks()
      setStatus('error')
      setErrorMessage(error instanceof Error ? error.message : '无法开始录制。')
      return false
    }
  }, [clearAudioMeter, clearTimer, kind, startAudioMeter, startElapsedTimer, stopTracks, supported])

  const stopRecording = useCallback(async () => {
    const recorder = recorderRef.current
    if (!recorder || (recorder.state !== 'recording' && recorder.state !== 'paused')) {
      throw new Error('当前没有正在进行的录制。')
    }

    finalizingActionRef.current = 'save'
    setStatus('processing')
    return new Promise<File>((resolve, reject) => {
      pendingStopRef.current = { resolve, reject }
      recorder.stop()
    })
  }, [])

  const pauseRecording = useCallback(() => {
    const recorder = recorderRef.current
    if (!recorder || recorder.state !== 'recording') {
      throw new Error('当前没有正在进行的录制。')
    }

    const segmentElapsed = startedAtRef.current > 0 ? Date.now() - startedAtRef.current : 0
    elapsedBeforePauseRef.current += segmentElapsed
    startedAtRef.current = 0
    setElapsedSeconds(Math.max(0, Math.floor(elapsedBeforePauseRef.current / 1000)))
    clearTimer()
    recorder.pause()
  }, [clearTimer])

  const resumeRecording = useCallback(() => {
    const recorder = recorderRef.current
    if (!recorder || recorder.state !== 'paused') {
      throw new Error('当前没有可继续的录制。')
    }

    startedAtRef.current = Date.now()
    startElapsedTimer()
    recorder.resume()
  }, [startElapsedTimer])

  const cancelRecording = useCallback(() => {
    const recorder = recorderRef.current
    finalizingActionRef.current = 'cancel'
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop()
      return
    }
    reset()
  }, [reset])

  useEffect(() => {
    return () => {
      clearTimer()
      clearAudioMeter()
      stopTracks()
      recorderRef.current = null
      pendingStopRef.current = null
    }
  }, [clearAudioMeter, clearTimer, stopTracks])

  return {
    supported,
    status,
    elapsedSeconds,
    audioLevels,
    errorMessage,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    cancelRecording,
    reset,
  }
}